import { FC } from 'react';
import { DateRangePicker } from 'rsuite';
import { Table, Column, HeaderCell, Cell } from 'rsuite-table';
import { RsInput } from 'components/Rsuite/Input';
import { RsButton } from 'components/Rsuite/Button';
import moment from 'moment';

interface ReportDataProps {
  reportData: {
    adPlatform: string;
    brandName: string;
    brandNo: string;
    budgetRate: number;
    ctime: string;
    endDate: string;
    logoUrl: string;
    markupRate: number;
    name: string;
    outboundCampaignId: string;
    planIssues: string;
    planStatus: string;
    primaryText: string;
    productIds: string[];
    rtbCampaignId: number;
    sellerId: string;
    sellerIssues: string;
    sellerStatus: string;
    startDate: string;
    targetBudget: number;
    utime: string;
  }[];
  reportFilter: {
    rtbCampaignId: string;
    sellerId: string;
    brandName: string;
    startDate: Date;
    endDate: Date;
  };
  onChange: (filter: any) => void;
  onSubmit: () => void;
  onReset: () => void;
}

enum SellerStatus {
  SELLER_CREATING = '賣家創建中',
  SELLER_INELIGIBLE = '賣家不合格',
  CREATING = '創建中',
  INELIGIBLE = '不合格',
  ACTIVE = '啟用',
  ERROR = '出錯誤',
  PAUSED = '暫停中',
  NOT_START = '未開始',
  RUNNING = '投放中',
  FINISHED = '已結束',
}

export const OutboundReport: FC<ReportDataProps> = ({
  reportData,
  reportFilter,
  onChange,
  onSubmit,
  onReset,
}) => {
  const dateFormat = 'YYYY-MM-DD';
  const handleChangeRtbCampaignId = (value) => {
    onChange({ ...reportFilter, rtbCampaignId: value });
  };
  const handleChangeSellerId = (value) => {
    onChange({ ...reportFilter, sellerId: value });
  };
  const handleChangeBrandName = (value) => {
    onChange({ ...reportFilter, brandName: value });
  };
  const handleChangeDate = (value) => {
    onChange({ ...reportFilter, startDate: value[0], endDate: value[1] });
  };
  const handleResetFilter = async () => {
    onReset();
  };

  return (
    <div>
      <div className='mb-4 w-full bg-white p-4 shadow-md'>
        <label className='mb-2 text-lg font-bold'>篩選器</label>
        <div className='grid grid-cols-6 gap-4'>
          <div>
            <label className='mb-1 text-gray-500'>站內活動ID</label>
            <RsInput
              value={reportFilter.rtbCampaignId}
              placeholder='請輸入站內活動ID'
              onChange={handleChangeRtbCampaignId}
            />
          </div>
          <div>
            <label className='mb-1 text-gray-500'>賣家ID</label>
            <RsInput
              value={reportFilter.sellerId}
              placeholder='請輸入賣家ID'
              onChange={handleChangeSellerId}
            />
          </div>
          <div>
            <label className='mb-1 text-gray-500'>品牌</label>
            <RsInput
              value={reportFilter.brandName}
              placeholder='請輸入品牌'
              onChange={handleChangeBrandName}
            />
          </div>
          <div>
            <label className='mb-1 text-gray-500'>活動區間</label>
            <DateRangePicker
              format='yyyy-MM-dd'
              value={[reportFilter.startDate, reportFilter.endDate]}
              onOk={handleChangeDate}
              cleanable={false}
              character=' – '
            />
          </div>
        </div>
        <div className='flex items-center justify-end gap-2'>
          <RsButton onClick={handleResetFilter}>還原</RsButton>
          <RsButton
            appearance='primary'
            onClick={() => {
              onSubmit();
            }}
          >
            篩選
          </RsButton>
        </div>
      </div>
      <Table
        data={reportData}
        bordered={false}
        cellBordered={false}
        hover={true}
        showHeader={true}
        autoHeight={true}
        maxHeight={440}
        rowHeight={80}
        className='shadow-md'
      >
        <Column width={100} verticalAlign={'middle'} align='center'>
          <HeaderCell>站內活動ID</HeaderCell>
          <Cell dataKey='rtbCampaignId' />
        </Column>
        <Column width={150} verticalAlign={'middle'} align='center'>
          <HeaderCell>開始日</HeaderCell>
          <Cell dataKey='startDate'>
            {(rowData) => {
              return (
                <span>{moment(rowData.startDate).format(dateFormat)}</span>
              );
            }}
          </Cell>
        </Column>
        <Column width={150} verticalAlign={'middle'} align='center'>
          <HeaderCell>結束日</HeaderCell>
          <Cell dataKey='endDate'>
            {(rowData) => {
              return <span>{moment(rowData.endDate).format(dateFormat)}</span>;
            }}
          </Cell>
        </Column>
        <Column width={170} verticalAlign={'middle'} fixed>
          <HeaderCell>Meta活動ID</HeaderCell>
          <Cell dataKey='outboundCampaignId'>
            {(rowData) =>
              rowData.outboundCampaignId === null
                ? '-'
                : rowData.outboundCampaignId
            }
          </Cell>
        </Column>
        <Column width={80} verticalAlign={'middle'} align='center' fixed>
          <HeaderCell>logo</HeaderCell>
          <Cell dataKey='logoUrl'>
            {(rowData) => {
              return (
                <img src={rowData.logoUrl} width={60} height={60} alt='logo' />
              );
            }}
          </Cell>
        </Column>
        <Column width={150} fullText verticalAlign={'middle'} align='center'>
          <HeaderCell>品牌</HeaderCell>
          <Cell dataKey='brandName' />
        </Column>
        <Column width={100} verticalAlign={'middle'} align='center'>
          <HeaderCell>Meta總預算</HeaderCell>
          <Cell dataKey='targetBudget' />
        </Column>
        <Column width={100} verticalAlign={'middle'} align='center'>
          <HeaderCell>Meta活動狀態</HeaderCell>
          <Cell dataKey='planStatus'>
            {(rowData) => {
              return SellerStatus[rowData.planStatus];
            }}
          </Cell>
        </Column>
        <Column width={100} verticalAlign={'middle'} align='center'>
          <HeaderCell>賣家狀態</HeaderCell>
          <Cell dataKey='sellerStatus'>
            {(rowData) => {
              return SellerStatus[rowData.sellerStatus];
            }}
          </Cell>
        </Column>
      </Table>
    </div>
  );
};
